import { useState, useEffect } from 'react'
import { isUndefined } from 'is-what'

type DebounceTuple<T> = [T | undefined, (val: T) => any, boolean?]

/**
 * Takes a tuple ([ state, fnOnState(state) ])
 * and performs an effect after the set period
 */
export const useDebounceEffect = <T>(
  state: DebounceTuple<T>[0],
  sideEffect: DebounceTuple<T>[1],
  ms = 1000
): boolean => {
  const [pending, setPending] = useState<boolean>(false)

  useEffect(() => {
    setPending(true)
    const handler = setTimeout(() => {
      !isUndefined(state) && sideEffect(state)
      setPending(false)
    }, ms)
    return () => clearTimeout(handler)
  }, [state, sideEffect, ms])

  return pending
}

export const useDebounce = <T>(value: T, delay = 1000): [T, boolean] => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const [isDebouncing, setIsDebouncing] = useState(false)

  useEffect(() => {
    setIsDebouncing(true)
    // Update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value)
      setIsDebouncing(false)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  return [debouncedValue, isDebouncing]
}

export const useDebounceOnChange = <T>(onChange: (val: T) => void, ms = 1000): DebounceTuple<T> => {
  const [value, setValue] = useState<T>()
  const pending = useDebounceEffect(value, onChange, ms)
  return [value, setValue, pending]
}
